import { Inject, Injectable, signal, computed } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { Observable, of } from 'rxjs';
import { tap, catchError, shareReplay } from 'rxjs/operators';
import { BaseHttpService } from '../../../modules/fwcore';
import { AQADocument, AQASearchRequest, AQASearchResponse, AQAThreadResponse } from '../models';


@Injectable({
  providedIn: 'root'
})
export class AQAService extends BaseHttpService {

  // Reactive state using signals
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);

  // Public readonly signals
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();

  constructor(
    http: HttpClient,
    @Inject(OKTA_AUTH) protected override auth: OktaAuth,
    @Inject('CONFIG_SETTINGS') private config: any
  ) {
    super(http, auth);

    const configSettings = config['general'];
    this.baseUrl = configSettings['aiSearchBaseUrl'];
    this.apiKey = "";
  }

  search(request: AQASearchRequest): Observable<AQASearchResponse> {
    this._loading.set(true);
    this._error.set(null);

    const token = this.getToken() ?? '';
    const url = `${this.baseUrl}/aqa/search`;

    return this.postAnyTyped<AQASearchResponse>(request, url, this.getHttpBearerHeaders(token));
  }

  getEmail(id: string): Observable<AQADocument> {
    const token = this.getToken() ?? '';
    const url = `${this.baseUrl}/aqa/email/${encodeURIComponent(id)}`;

    return this.getItem<AQADocument>(url, this.getHttpBearerHeaders(token)).pipe(
      catchError(error => {
        this._error.set(this.extractErrorMessage(error));
        throw error;
      })
    );
  }

  getThread(threadId: string): Observable<AQAThreadResponse> {
    const token = this.getToken() ?? '';
    const url = `${this.baseUrl}/aqa/thread/${encodeURIComponent(threadId)}`;

    return this.getItem<AQAThreadResponse>(url, this.getHttpBearerHeaders(token)).pipe(
      catchError(error => {
        this._error.set(this.extractErrorMessage(error));
        throw error;
      })
    );
  }

  getAttachment(id: string, attachmentName:string): Observable<any> {
    const token = this.getToken() ?? '';
    const url = `${this.baseUrl}/aqa/email/${id}/content?attachmentName=${encodeURIComponent(attachmentName)}`;

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'X-Skip-Error-Handler': 'true'
    });

    console.log('Fetching document...');

    return this.http.get(url, { headers, responseType: 'blob', observe: 'response' });
  }

  getAttachmentByStoragePath(storagePath: string, attachmentName:string): Observable<any> {
    const token = this.getToken() ?? '';
    const url = `${this.baseUrl}/aqa/email/content?storagePath=${storagePath}&attachmentName=${encodeURIComponent(attachmentName)}`;

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'X-Skip-Error-Handler': 'true'
    });

    console.log('Fetching document...');

    return this.http.get(url, { headers, responseType: 'blob', observe: 'response' });
  }

  private extractErrorMessage(error: unknown): string {
    if (error && typeof error === 'object') {
      const err = error as Record<string, unknown>;
      if (err['error'] && typeof err['error'] === 'object') {
        const innerError = err['error'] as Record<string, unknown>;
        if (typeof innerError['message'] === 'string') {
          return innerError['message'];
        }
      }
      if (typeof err['message'] === 'string') {
        return err['message'];
      }
      if (err['status'] === 0) {
        return 'Unable to connect to the server.';
      }
      if (err['status'] === 503) {
        return 'Service temporarily unavailable.';
      }
    }
    return 'An unexpected error occurred.';
  }
}
